/**
 * [身份证信息]
 * @author: Fu Xiaochun
 * @date: 2022-11-22 
 */

import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import styles from './index.module.scss';
import { Input, Select, message, DatePicker, Checkbox } from 'antd';
import CusotmUpload, { generateFileList } from '@/components/NewUpload';
import moment from 'moment';
import { getOcrIdentityInfo } from '@/apis/common/index';
import { GenerateUUID } from '@/util/utils';
import PropTypes from 'prop-types';

const { Option } = Select;

function IdInfo(props, ref) {

    // 身份证信息
    const [faceImg, setFaceImg] = useState(); // 身份证头像面图
    const [emblemImg, setEmblemImg] = useState(); // 身份证国徽面图
    const [userInfo, setUserInfo] = useState({
        name: '',
        id: '',
        gender: '',
        birthDay: '',
        address: '',
        authority: '', // 签发机关
        startDate: '', // 证件签发日期
        validateDate: ''  // 证件有效期
    });

    const genders = [{ code: 0, value: '女' }, { code: 1, value: '男' }];

    useEffect(() => {
        if (props.detail.id) {
            initData();
        }
    }, [props.detail]);

    const initData = () => {
        let identityInfo = props.detail.identityInfo || {};
        setFaceImg({ uid: GenerateUUID(), url: identityInfo.cardFront });
        setEmblemImg({ uid: GenerateUUID(), url: identityInfo.cardBack });
        setUserInfo({
            name: identityInfo.realName,
            id: identityInfo.id,
            gender: identityInfo.sex,
            birthDay: identityInfo.birthday,
            address: identityInfo.address,
            // authority: identityInfo.xx, // 签发机关
            startDate: identityInfo.issueData, // 证件签发日期
            validateDate: identityInfo.expireData  // 证件有效期
        });
    };

    // 身份证识别
    const getIdentityInfoByOCR = (url, type) => {
        let params = { [type]: url };
        getOcrIdentityInfo(params).then(res => {
            let data = res.result;
            let temp = { ...userInfo };
            if (type == 'frontImgUrl') {
                temp.name = data.realName;
                temp.id = data.cardNum;
                temp.gender = data.sex;
                temp.birthDay = data.birthday.split(' ')[0];
                temp.address = data.address;
                props.setBankAccountName(data.realName);
            }
            if (type == 'backImgUrl') {
                temp.authority = data.authority;
                temp.startDate = data.startDate;
                temp.validateDate = data.validateDate;
            }

            setUserInfo(temp);
        }).catch(err => {
            message.error(err.message);
        });
    };

    const faceImgChange = (data = {}) => {
        let imgs;
        if (Array.isArray(data.fileList)) {
            data.fileList.forEach(v => {
                if (v.status == 'success') {
                    imgs = v;
                }
            });
        }
        if (imgs) {
            getIdentityInfoByOCR(imgs.url, 'frontImgUrl');
            setFaceImg(imgs);
        }
    };

    const emblemImgChange = (data = {}) => {
        let imgs;
        if (Array.isArray(data.fileList)) {
            data.fileList.forEach(v => {
                if (v.status == 'success') {
                    imgs = v;
                }
            });
        }
        if (imgs) {
            getIdentityInfoByOCR(imgs.url, 'backImgUrl');
            setEmblemImg(imgs);
        }
    };

    const onUserInfoChange = (e) => {
        let val = e.target.value.trim();
        let key = e.target.name;
        let temp = { ...userInfo };
        temp[key] = val;
        setUserInfo(temp);
        if (key == 'name') {
            props.setBankAccountName(val);
        }
    };

    const onBirthChange = (date, dateStr) => {
        let temp = { ...userInfo };
        temp.birthDay = dateStr;
        setUserInfo(temp);
    };

    const onGenderChange = (val) => {
        let temp = { ...userInfo };
        temp.gender = val;
        setUserInfo(temp);
    };

    const disabledDate = (current) => {
        return current && current > moment().endOf('day');
    };

    const validityDisabledDate = (current) => {
        return current && current < moment().endOf('day');
    };

    const onStartDateChange = (date, dateStr) => {
        let temp = { ...userInfo };
        temp.startDate = dateStr;
        setUserInfo(temp);
    };

    const onValidateDateChange = (date, dateStr) => {
        let temp = { ...userInfo };
        temp.validateDate = dateStr;
        setUserInfo(temp);
    };

    useImperativeHandle(ref, () => ({
        getState: () => {
            return {
                cardFront: faceImg?.url,
                cardBack: emblemImg?.url,
                realName: userInfo.name,
                sex: userInfo.gender,
                birthday: userInfo.birthDay ? userInfo.birthDay.replace(/-/g, '') : undefined,
                cardNum: userInfo.id,
                address: userInfo.address,
                issueData: userInfo.startDate ? userInfo.startDate.replace(/-/g, '') : undefined,
                expireData: userInfo.validateDate ? userInfo.validateDate.replace(/-/g, '') : undefined,
                authority: userInfo.authority,
                subjectType: 'TRANSPORT',
            };
        }
    }));

    return (
        <div className={styles.idInfo}>
            <div className={styles.idcard}>
                <div className={styles.label}>身份证信息：</div>
                <div className={styles.idcardImgs}>
                    <div className={styles.cardItem}>
                        <CusotmUpload accept='.png, .jpg, .jpeg' listType='picture-card' wallMode='single'
                            fileList={faceImg ? [faceImg] : null}
                            onFileListChange={faceImgChange}
                            removeError
                            qiniuUploadProps={{ uploadType: 'image' }}
                            beforeUpload={file => {
                                const isType = ['image/png', 'image/jpeg', 'image/jpg'];
                                const isLt5M = file.size / 1024 / 1024 > 5;
                                if (!isType.includes(file.type)) {
                                    message.warn('只能上传png,jpg, jpeg格式的图片!');
                                    file.status = 'error';
                                    return false;
                                } else if (isLt5M) {
                                    message.warn('图片必须小于 5MB!');
                                    file.status = 'error';
                                    return false;
                                }
                            }}
                        >
                            <div style={{ color: '#999999', fontSize: '16px' }}>上传身份证人像面</div>
                        </CusotmUpload>
                    </div>
                    <div className={styles.cardItem}>
                        <CusotmUpload accept='.png, .jpg, .jpeg' listType='picture-card' wallMode='single'
                            fileList={emblemImg ? [emblemImg] : null}
                            onFileListChange={emblemImgChange}
                            removeError
                            qiniuUploadProps={{ uploadType: 'image' }}
                            beforeUpload={file => {
                                const isType = ['image/png', 'image/jpeg', 'image/jpg'];
                                const isLt5M = file.size / 1024 / 1024 > 5;
                                if (!isType.includes(file.type)) {
                                    message.warn('只能上传png,jpg, jpeg格式的图片!');
                                    file.status = 'error';
                                    return false;
                                } else if (isLt5M) {
                                    message.warn('图片必须小于 5MB!');
                                    file.status = 'error';
                                    return false;
                                }
                            }}
                        >
                            <div style={{ color: '#999999', fontSize: '16px' }}>上传身份证国徽面</div>
                        </CusotmUpload>
                    </div>
                </div>
            </div>
            <div className={styles.idDetail}>
                <div className={styles.item}>
                    <div className={styles.label}>身份证姓名：</div>
                    <div className={styles.val}>
                        <Input className={styles.txt} allowClear name="name" value={userInfo.name} placeholder="请输入身份证姓名" maxLength={30} onChange={onUserInfoChange} />
                    </div>
                </div>
                <div className={styles.item}>
                    <div className={styles.label}>出生年月日：</div>
                    <div className={styles.val}>
                        <DatePicker className={styles.txt} disabledDate={disabledDate} value={userInfo.birthDay ? moment(userInfo.birthDay, 'YYYY-MM-DD') : ''} onChange={onBirthChange} format={'YYYY-MM-DD'} />
                    </div>
                </div>
                <div className={styles.item}>
                    <div className={styles.label}>性别：</div>
                    <div className={styles.val}>
                        <Select className={styles.txt} placeholder='请选择性别' onChange={onGenderChange} value={userInfo.gender}>
                            {genders.map(item => <Option key={item.code} value={item.code} >{item.value}</Option>)}
                        </Select>
                    </div>
                </div>
                <div className={styles.item}>
                    <div className={styles.label}>身份证号码：</div>
                    <div className={styles.val}>
                        <Input className={styles.txt} allowClear name="id" value={userInfo.id} maxLength={18} onChange={onUserInfoChange} />
                    </div>
                </div>
                <div className={styles.item}>
                    <div className={styles.label}>签发机关：</div>
                    <div className={styles.val}>
                        <Input className={styles.txt} allowClear name="authority" value={userInfo.authority} maxLength={20} onChange={onUserInfoChange} />
                    </div>
                </div>
                <div className={styles.item}>
                    <div className={styles.label}>有效期限：</div>
                    <div className={styles.val}>
                        <DatePicker style={{ width: '130px' }} value={userInfo.startDate ? moment(userInfo.startDate, 'YYYY-MM-DD') : ''} onChange={onStartDateChange} format={'YYYY-MM-DD'} />
                        <span style={{ display: 'inline-block', width: '10px', textAlign: 'center' }}>-</span>
                        <DatePicker style={{ width: '130px' }} value={userInfo.validateDate ? moment(userInfo.validateDate, 'YYYY-MM-DD') : ''} onChange={onValidateDateChange} format={'YYYY-MM-DD'} />
                    </div>
                </div>
                <div className={styles.item} style={{ width: '100%' }}>
                    <div className={styles.label}>身份证住址：</div>
                    <div className={styles.val}>
                        <Input className={styles.txt} allowClear name="address" value={userInfo.address} maxLength={50} onChange={onUserInfoChange} />
                    </div>
                </div>
            </div>
        </div>
    );
}


const RefIdInfo = forwardRef(IdInfo);

IdInfo.propTypes = {
    detail: PropTypes.object,
};

IdInfo.defaultProps = {
    detail: {}
};


export default RefIdInfo;